/**
 * When a scroll should turn into a fetch.
 *
 * A scroll container emits an event per frame. Planning a window and issuing a
 * request on each one would fetch ranges the reader has already scrolled past by
 * the time they arrive, so the decision is throttled -- but not by a plain
 * timer, because the interesting case is *how fast* the reader is moving. Somebody
 * dragging the scrollbar down a hundred thousand rows wants exactly one request,
 * for where they stop; somebody nudging the wheel wants the next block now.
 *
 * Ported from upstream's `fetchByScrollTopDelayed`, with the state made explicit
 * and the decision made pure. Upstream keeps `distance`, `last`, `processing` and
 * a timeout id on a module-level object and mutates them in place, which is both
 * untestable and shared between every grid on the page. Here the caller holds the
 * state, passes the clock in, and gets back a decision plus the next state -- so
 * the policy is testable against plain numbers, and the only thing left to hold
 * is the one timeout.
 */

/** How fast is too fast to fetch, in pixels per millisecond. */
export const MAX_FETCH_VELOCITY = 2.5

/** The shortest gap between two fetch decisions, in milliseconds. */
export const SCROLL_INTERVAL = 100

export interface ScrollScheduleState {
  /** The scroll position the last decision was measured from. */
  scrollTop: number
  /** When that measurement was taken. */
  at: number
  /** Whether a fetch this scheduler started is still outstanding. */
  fetching: boolean
}

export const initialScrollSchedule = (now: number): ScrollScheduleState => ({
  scrollTop: 0,
  at: now,
  fetching: false,
})

export type ScrollDecision =
  /** Plan and fetch now. */
  | { fire: true }
  /** Come back in this many milliseconds and decide again. */
  | { fire: false; deferMs: number }

export interface ScrollSchedulePlan {
  decision: ScrollDecision
  state: ScrollScheduleState
}

/**
 * Decides what to do about a scroll position, and what to remember.
 *
 * Fires immediately only when all three hold: no fetch is outstanding, at least
 * `SCROLL_INTERVAL` has passed since the last decision, and the reader is moving
 * slowly enough that where they are now is probably where they want to be.
 * Otherwise it defers, which the caller implements as a trailing timeout -- so a
 * fast drag produces one fetch, at the end, for the range under the thumb when it
 * stopped.
 *
 * The baseline moves on a deferral too, once the interval has passed. Without
 * that, velocity would be measured against a position from the beginning of a
 * long drag and would keep growing forever, so a reader who slowed down to a stop
 * would never be recognised as having stopped.
 */
export function planScrollFire(
  state: ScrollScheduleState,
  { scrollTop, now }: { scrollTop: number; now: number },
): ScrollSchedulePlan {
  const elapsed = now - state.at
  const distance = Math.abs(scrollTop - state.scrollTop)
  // Two events in the same millisecond divide by zero, which is `Infinity` for
  // any real movement and `NaN` for none. Neither is guarded, because neither is
  // consulted: `elapsed` has to clear the interval first, and zero does not.
  const velocity = distance / elapsed

  if (!state.fetching && elapsed > SCROLL_INTERVAL && velocity < MAX_FETCH_VELOCITY) {
    return {
      decision: { fire: true },
      state: { scrollTop, at: now, fetching: state.fetching },
    }
  }

  return {
    decision: { fire: false, deferMs: SCROLL_INTERVAL },
    state: elapsed > SCROLL_INTERVAL ? { ...state, scrollTop, at: now } : state,
  }
}
